import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { faFileInvoiceDollar, faFileSignature, faSave, faSquareEnvelope, faTimes, faTreasureChest, faCar, faFileAlt } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import NotificationToast from "../../components/Helpers/NotificationToast";
import { openModal } from "../../store/modal/modalReducer";
import { Formik, Form, Field } from "formik";
import SubmitButton from "../Forms/SubmitButton";
import CustomButton from "../Helpers/CustomButton";
import { getBooking } from "../../store/booking/bookingActions";
import QuickFormText from "../Forms/QuickFormText";
import QuickFormSelect from "../Forms/QuickFormSelect";
import LoadingComponent from "../Helpers/LoadingComponent";
import QuickFormDate from "../Forms/QuickFormDate";
import SettingsSwitch from "../Forms/SettingsSwitch";
import moment from "moment";
import SettingsSelect from "../Forms/SettingsSelect";

export default function GridInvoiceInformation({ setDoc, db, setIgnoreRefresh }) {
    const dispatch = useDispatch();
    const history = useHistory();
    const [booking, setBooking] = useState([]);
    const { current_booking } = useSelector((state) => state.booking);
    const { current_staff, permissions } = useSelector((state) => state.auth);
    const canEditApplicantDetails = permissions?.includes('edit_applicant_details') || false;
    // Tenant-only chats (not lease holders) only see building/unit type/unit + update button
    const isTenantOnly = current_booking?.tenant_type === 'TN';
    const [units, setUnits] = useState([]);
    const [beds, setBeds] = useState([]);
    const [unitTypes, setUnitTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [invoiceButtonLoading, setInvoiceButtonLoading] = useState(false);
    const [nsfasButtonLoading, setNsfasButtonLoading] = useState(false);
    const [emailButtonLoading, setEmailButtonLoading] = useState(false);
    const [leaseSendButtonLoading, setLeaseSendButtonLoading] = useState(false);
    const [selectedPropertyId, setSelectedPropertyId] = useState(null);
    const [selectedCarport, setSelectedCarport] = useState(null);
    const [selectedFloorId, setSelectedFloorId] = useState(null);
    var invoiceButtonLoadingText = "Generating Invoice";
    var emailButtonLoadingText = "Sending Invoice and Lease";
    var leaseButtonLoadingText = "Generating Lease";
    var suretyButtonLoadingText = "Generating Surety";
    const [leaseButtonLoading, setLeaseButtonLoading] = useState(false);
    const [suretyButtonLoading, setSuretyButtonLoading] = useState(false);
    const [leaseAdButtonLoading, setLeaseAdButtonLoading] = useState(false);
    const [showOldLeases, setShowOldLeases] = useState(false);
    const [oldRates, setOldRates] = useState([]);
    const [downloadingRateId, setDownloadingRateId] = useState(null);
    const { properties } = useSelector((state) => state.properties);
    const [leaseTermMonths, setLeaseTermMonths] = useState(12);

    const { type } = useParams();
    var url_string = window.location.href;
    var url = new URL(url_string);
    var tag_id = url.searchParams.get("tag");

    var month_strt = moment().add(1, "M").format("MM");
    var year_strt = moment().format("Y");

    if (process.env.REACT_APP_COLOUR === "QL") {
        if (moment().format("DD") < "09") {
            month_strt = moment().format("MM");
        }
    }

    const leaseTerms = [
        { value: 0, label: "" },
        { value: 1, label: "Flexi Lease" },
        { value: 2, label: "1 Year" },
        { value: 3, label: "2 Years" },
        { value: 4, label: "3 Years" },
        { value: 5, label: "4 Years" },
        { value: 6, label: "5+ Years" },
    ];

    const depositList = [
        { value: 0, label: "" },
        { value: 1, label: "Flexi / 1 Year Lease = 1 Month Rent" },
        { value: 2, label: "2+ Years Lease = 2 Months Exit Rent" },
    ];

    const depositTypes = [
        { value: 0, label: "Full" },
        { value: 1, label: "50%" },
        { value: 2, label: "Pay off over 2 months" },
        { value: 3, label: "Pay off over 12 months" },
        { value: 4, label: "Custom" },
    ];

    const calculateDeposit = (depositType, monthlyRental) => {
        const rental = parseFloat(String(monthlyRental).replace(/,/g, "")) || 0;
        switch (depositType) {
            case 0: // Full
                return rental;
            case 1: // 50%
                return rental * 0.5;
            case 2: // Pay off over 2 months
                return rental / 2;
            case 3: // Pay off over 12 months
                return rental / 12;
            case 4: // Custom
            default:
                return null; // Don't auto-calculate for custom
        }
    };

    var lease_start_string = year_strt + "-" + month_strt + "-01";

    var month_end = moment().format("MM");
    var year_end = moment().add(1, "Y").format("Y");

    var lease_end_string = moment(new Date(year_end, month_end, 0)).format("Y-MM-D");

    if (process.env.REACT_APP_COLOUR === "UC") {
        lease_start_string = "2025-02-01";
        lease_end_string = "2025-11-30";
    }

    useEffect(() => {
        const fetchParkingBays = async () => {
            if (!selectedPropertyId) {
                return;
            }

            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}parking-bays?property_id=${selectedPropertyId}`);
                const bays = response.data;

                // If we have a parking bay ID in the current rate, set it as the selected value
                if (current_booking?.rates?.[current_booking.rates.length - 1]?.parking_bay_id) {
                    const parkingBayId = current_booking.rates[current_booking.rates.length - 1].parking_bay_id.toString();
                }
            } catch (error) {
                // Handle error silently
            }
        };

        // Run immediately if we have a property ID
        if (selectedPropertyId) {
            fetchParkingBays();
        }
    }, [selectedPropertyId, current_booking?.property_id, current_booking?.rates]);

    // Add a new useEffect to handle initial property ID setup
    useEffect(() => {
        if (current_booking?.property_id && !selectedPropertyId) {
            setSelectedPropertyId(current_booking.property_id);
        }
    }, [current_booking?.property_id]);

    useEffect(() => {
        // Initialize selectedCarport from current_booking
        if (current_booking?.rates?.[current_booking.rates.length - 1]?.carport && !selectedCarport) {
            const carport = current_booking.rates[current_booking.rates.length - 1].carport;
            setSelectedCarport(carport);
        }
    }, [current_booking?.rates]);

    useEffect(() => {
        const fetchFloorId = async () => {
            if (!selectedPropertyId) {
                setSelectedFloorId(null);
                return;
            }

            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}property_floors/${selectedPropertyId}`);
                const floors = response.data;

                // Find the first parking floor
                const parkingFloor = floors.find((floor) => floor.is_parking === 1);
                if (parkingFloor) {
                    setSelectedFloorId(parkingFloor.id);
                } else {
                    setSelectedFloorId(null);
                }
            } catch (error) {
                setSelectedFloorId(null);
            }
        };

        fetchFloorId();
    }, [selectedPropertyId]);

    useEffect(async () => {
        getLocalBooking();
        if (current_booking.property_id) {
            getUnits(current_booking.property_id);
            getUnitTypes(current_booking.property_id);
        }
    }, [setBooking]);

    async function getLocalBooking() {
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}user_profile/${current_booking.user.id}`,
        }).then((response) => {
            setBooking(response.data);
            setLoading(false);
        });
    }

    async function getUnits(id) {
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}units/${id}`,
        }).then((response) => {
            setUnits(response.data);
        });
    }

    // async function getUnits(id) {
    //     await axios({
    //         method: "get",
    //         url: `${process.env.REACT_APP_API_URL}beds/${unit_id}`,
    //     }).then((response) => {
    //         setUnits(response.data);
    //     });
    // }

    async function getUnitTypes(id) {
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}unit_types/${id}`,
        }).then((response) => {
            setUnitTypes(response.data);
        });
    }

    if (loading === true) {
        return <LoadingComponent />;
    }

    function formatToFloat(value) {
        if (value) {
            if (typeof value === "number") {
                return value;
            }
            return parseFloat(value.replace(/,/g, ""));
        } else {
            return value;
        }
    }

    function email_documents() {
        var button = setEmailButtonLoading;
        button(true);
        axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}email_documents/${current_booking.id}`,
            data: { current_staff: current_staff },
        })
            .then((response) => {
                toast(<NotificationToast title={"Documents emailed"} message={"The email has sent succesfully."} />);
            })
            .catch((response) => {
                toast(<NotificationToast title={"Email failed"} message={response.response.data} type="error" />);
            })
            .finally(() => {
                button(false);
            });
    }

    async function re_generate_addendum(booking_id) {
        axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}regen_addendum/${current_booking.id}`,
        })
            .then((response) => {
                toast(<NotificationToast title={type + " generated"} message={"You can now send the link to the applicant."} />);
                setIgnoreRefresh(true);
                dispatch(getBooking(current_booking.id));
            })
            .catch((response) => {
                toast(<NotificationToast title={type + " failed"} message={response.response.data} list={true} type="error" />);
            });
    }

    async function download_lease(rateId) {
        try {
            setDownloadingRateId(rateId);
            const response = await axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}download_lease/${current_booking.id}/${rateId}`,
                responseType: "blob", // Important for downloading files
            });

            // Create a blob URL for the file
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;

            // Set the filename for the download
            const fileName = `Lease_${current_booking.id}_${rateId}.pdf`;
            link.setAttribute("download", fileName);

            // Trigger the download
            document.body.appendChild(link);
            link.click();

            // Clean up
            link.remove();
            window.URL.revokeObjectURL(url);

            toast(<NotificationToast title="Download Started" message="The lease download has started." />);
        } catch (error) {
            console.error("Error downloading lease:", error);
            toast(<NotificationToast title="Download Failed" message={error.response?.data || "An error occurred while downloading the lease."} type="error" />);
        } finally {
            setDownloadingRateId(null);
        }
    }

    async function generate_document(type, rateId = null) {
        let type2 = type;
        if (type === "invoice" || type === "send_invoice") {
            var button = setInvoiceButtonLoading;
            type = "invoice";
        } else if (type === "lease" || type === "lease_send") {
            var button = setLeaseButtonLoading;
            type = "lease";
        } else if (type === "surety") {
            var button = setSuretyButtonLoading;
        } else {
            var button = setLeaseAdButtonLoading;
        }

        button(true);

        // Build URL with send_template parameter for lease_send
        let url;
        if (rateId) {
            url = `${process.env.REACT_APP_API_URL}generate_document/${type}/${current_booking.id}?rate_id=${rateId}`;
        } else {
            url = `${process.env.REACT_APP_API_URL}generate_document/${type}/${current_booking.id}`;
        }

        // Add send_template parameter for lease_send (generates PDF and sends template in one job)
        if (type2 === "lease_send") {
            url += (url.includes("?") ? "&" : "?") + "send_template=1";
        }

        axios({
            method: "post",
            url: url,
        })
            .then((response) => {
                if (type2 === "lease_send") {
                    toast(<NotificationToast title="Lease queued" message="The lease is being generated and will be sent automatically when ready." />);
                } else {
                    toast(<NotificationToast title={type + " generated"} message={"You can now send the link to the applicant."} />);
                }
                // setIgnoreRefresh(true);
                // dispatch(getBooking(current_booking.id));

                // Note: lease_send now handled by backend job - no separate send_lease call needed

                // Send Invoice?
                if (type2 === "send_invoice") {
                    axios({
                        method: "post",
                        url: `${process.env.REACT_APP_API_URL}send_invoice/${current_booking?.user?.id}/${current_booking.id}`,
                    })
                        .then((response) => {
                            toast(<NotificationToast title={"Invoice sent"} message={"The invoice has been sent."} />);
                        })
                        .catch((response) => {
                            toast(<NotificationToast title={"Invoice failed"} message={response.response.data} list={true} type="error" />);
                        });
                }
            })
            .catch((response) => {
                toast(<NotificationToast title={type + " failed"} message={response.response.data} list={true} type="error" />);
            })
            .finally(() => {
                button(false);
            });
    }

    var default_values = {
        refuse: current_booking.property?.refuse_fee || 0,
        effluent: current_booking.property?.effluent_fee || 0,
        parking_fee: current_booking.property?.parking_fee || 0,
    };

    async function make_nsfas_pricing() {
        setNsfasButtonLoading(true);
        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}update_nsfas_pricing`,
            data: {
                current_staff: current_staff,
                current_booking_id: current_booking.id,
                user_id: current_booking?.user?.id,
            },
        })
            .then((response) => {
                toast(<NotificationToast title={"Pricing updated"} message={"The pricing has been updated succesfully."} />);
                setIgnoreRefresh(true);
                dispatch(getBooking(current_booking.id));
            })
            .catch((response) => {
                toast(<NotificationToast title={"Pricing Failed failed"} message={response.response.data} type="error" />);
            })
            .finally(() => {
                setNsfasButtonLoading(false);
            });
    }

    const send_lease_pdf = () => {
        setLeaseSendButtonLoading("Sending Lease PDF");
        toast(<NotificationToast type="info" message="Sending Lease PDF..." />, {
            hideProgressBar: true,
            autoClose: 2000,
        });
        axios
            .post(`${process.env.REACT_APP_API_URL}send_lease_pdf/${current_booking?.user?.id}/${current_booking.id}`)
            .then((response) => {
                setLeaseSendButtonLoading(false);
                toast(<NotificationToast type="success" message="Lease PDF queued for sending" />, {
                    hideProgressBar: true,
                });
            })
            .catch((error) => {
                setLeaseSendButtonLoading(false);
                const errorMessage = error.response?.data?.error || "Failed to send lease PDF";
                toast(<NotificationToast type="error" message={errorMessage} />, {
                    hideProgressBar: true,
                });
            });
    };

    const send_invoice_pdf = () => {
        setInvoiceButtonLoading(true);
        toast(<NotificationToast type="info" message="Sending Invoice PDF..." />, {
            hideProgressBar: true,
            autoClose: 2000,
        });
        axios
            .post(`${process.env.REACT_APP_API_URL}send_invoice_pdf/${current_booking?.user?.id}/${current_booking.id}`)
            .then((response) => {
                setInvoiceButtonLoading(false);
                toast(<NotificationToast type="success" message="Invoice PDF queued for sending" />, {
                    hideProgressBar: true,
                });
            })
            .catch((error) => {
                setInvoiceButtonLoading(false);
                const errorMessage = error.response?.data?.error || "Failed to send invoice PDF";
                toast(<NotificationToast type="error" message={errorMessage} />, {
                    hideProgressBar: true,
                });
            });
    };

    return (
        <div className="h-96 overflow-y-scroll scroll-smooth">
            {process.env.REACT_APP_COLOUR === "QL" && !isTenantOnly && (
                <CustomButton
                    text="Parking Plate"
                    styling="w-full mb-4"
                    icon={faCar}
                    onClick={() =>
                        dispatch(
                            openModal({
                                modal_type: "ModalParkingPlate",
                                modal_props: {
                                    current_booking,
                                    current_staff,
                                    properties,
                                },
                            }),
                        )
                    }
                />
            )}

            {/* Start Information */}
            <Formik
                enableReinitialize
                initialValues={{
                    property_id: current_booking.property_id,
                    unit_type: current_booking.unit_type && current_booking.unit_type.id ? current_booking.unit_type.id : current_booking.unit_type,
                    unit_id: current_booking.unit_id,
                    joint_application: current_booking.joint_application,
                    invoice_monthly_total:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].invoice_monthly_total
                            ? current_booking.rates[current_booking.rates.length - 1].invoice_monthly_total
                            : "",
                    invoice_monthly_hide:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].invoice_monthly_hide
                            ? current_booking.rates[current_booking.rates.length - 1].invoice_monthly_hide
                            : "",
                    // parking_fee: default_values?.parking_fee ? default_values.parking_fee : "",
                    parking_fee:
                        current_booking.rates && current_booking.rates.length > 0 && current_booking.rates[current_booking.rates.length - 1].parking_fee
                            ? current_booking.rates[current_booking.rates.length - 1].parking_fee
                            : default_values?.parking_fee
                              ? default_values.parking_fee
                              : "",
                    monthly_rental:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].monthly_rental
                            ? current_booking.rates[current_booking.rates.length - 1].monthly_rental
                            : "",
                    monthly_rental_wording: current_booking.rates[current_booking.rates.length - 1] ? current_booking.rates[current_booking.rates.length - 1].monthly_rental_wording : "",
                    escalation:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].escalation
                            ? current_booking.rates[current_booking.rates.length - 1].escalation
                            : "",
                    lease_term:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].lease_term
                            ? current_booking.rates[current_booking.rates.length - 1].lease_term
                            : 0,
                    deposit_type:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].deposit_type !== undefined
                            ? current_booking.rates[current_booking.rates.length - 1].deposit_type
                            : 0,
                    admin_fee:
                        current_booking.rates[current_booking.rates.length - 1] &&
                        current_booking.rates[current_booking.rates.length - 1].admin_fee !== undefined &&
                        current_booking.rates[current_booking.rates.length - 1].admin_fee !== null
                            ? current_booking.rates[current_booking.rates.length - 1].admin_fee
                            : process.env.REACT_APP_COLOUR === "QL"
                              ? 1000
                              : "",
                    no_admin_fee: current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].admin_fee === 0 ? 1 : 0,
                    deposit_price:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].deposit_price
                            ? current_booking.rates[current_booking.rates.length - 1].deposit_price
                            : current_booking.rates[current_booking.rates.length - 1]?.monthly_rental || "",
                    surance_amount:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].surance_amount
                            ? current_booking.rates[current_booking.rates.length - 1].surance_amount
                            : "",
                    booking_fee_price:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].booking_fee_price
                            ? current_booking.rates[current_booking.rates.length - 1].booking_fee_price
                            : "",
                    key_deposit:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].key_deposit
                            ? current_booking.rates[current_booking.rates.length - 1].key_deposit
                            : process.env.REACT_APP_COLOUR === "QL"
                              ? 200
                              : "",
                    carport: current_booking.rates[current_booking.rates.length - 1] ? current_booking.rates[current_booking.rates.length - 1].carport : "",
                    special_wording: current_booking.rates[current_booking.rates.length - 1] ? current_booking.rates[current_booking.rates.length - 1].special_wording : "",
                    // effluent: default_values?.effluent ? default_values.effluent : "",
                    effluent:
                        current_booking.rates && current_booking.rates.length > 0 && current_booking.rates[current_booking.rates.length - 1].effluent
                            ? current_booking.rates[current_booking.rates.length - 1].effluent
                            : default_values?.effluent
                              ? default_values.effluent
                              : "",
                    lease_surance: current_booking.rates[current_booking.rates.length - 1] ? current_booking.rates[current_booking.rates.length - 1].lease_surance : "",
                    has_gas:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].has_gas
                            ? current_booking.rates[current_booking.rates.length - 1].has_gas
                            : 0,
                    // refuse: default_values?.refuse ? default_values.refuse : "",
                    refuse:
                        current_booking.rates && current_booking.rates.length > 0 && current_booking.rates[current_booking.rates.length - 1].refuse
                            ? current_booking.rates[current_booking.rates.length - 1].refuse
                            : default_values?.refuse
                              ? default_values.refuse
                              : "",
                    special_deduction: current_booking.rates[current_booking.rates.length - 1] ? current_booking.rates[current_booking.rates.length - 1].special_deduction : "",
                    lease_start:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].lease_start
                            ? current_booking.rates[current_booking.rates.length - 1].lease_start
                            : lease_start_string,
                    lease_end:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].lease_end
                            ? current_booking.rates[current_booking.rates.length - 1].lease_end
                            : lease_end_string,
                    occupation_date:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].occupation_date
                            ? current_booking.rates[current_booking.rates.length - 1].occupation_date
                            : "",
                    size_required: current_booking.user.premises ? current_booking.user.premises.size_required : "",

                    paid:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].paid
                            ? current_booking.rates[current_booking.rates.length - 1].paid
                            : "",
                    paid_at:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].paid_at
                            ? current_booking.rates[current_booking.rates.length - 1].paid_at
                            : "",
                    deposit_paid:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].deposit_paid
                            ? current_booking.rates[current_booking.rates.length - 1].deposit_paid
                            : "",
                    deposit_paid_at:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].deposit_paid_at
                            ? current_booking.rates[current_booking.rates.length - 1].deposit_paid_at
                            : "",
                    partially_paid:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].partially_paid
                            ? current_booking.rates[current_booking.rates.length - 1].partially_paid
                            : "",
                    partially_paid_at:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].partially_paid_at
                            ? current_booking.rates[current_booking.rates.length - 1].partially_paid_at
                            : "",
                    signed_at:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].signed_at
                            ? current_booking.rates[current_booking.rates.length - 1].signed_at
                            : "",
                    signed_lease_at:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].signed_lease_at
                            ? current_booking.rates[current_booking.rates.length - 1].signed_lease_at
                            : "",
                    signed_lease_joint_at:
                        current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].signed_lease_joint_at
                            ? current_booking.rates[current_booking.rates.length - 1].signed_lease_joint_at
                            : "",

                    override_property_int: current_booking.property_override === null ? null : 1,
                    property_override: current_booking.property_override,
                    price_override: current_booking.rates[current_booking.rates.length - 1]?.price_override ? current_booking.rates[current_booking.rates.length - 1].price_override : 0,
                    lease_dates_override: current_booking.rates[current_booking.rates.length - 1]?.lease_dates_override
                        ? current_booking.rates[current_booking.rates.length - 1].lease_dates_override
                        : 0,
                    special_payment_date: current_booking.special_payment_date ? current_booking.special_payment_date : null,
                }}
                onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
                    let override_property = null;
                    if (process.env.REACT_APP_COLOUR === "UC") {
                        if (values.override_property_int === 1) {
                            override_property = values.property_override;
                        } else {
                            override_property = -1;
                        }
                    }

                    //Calculate Admin Fee
                    if (values.no_admin_fee == 1) {
                        values.admin_fee = 0;
                    } else {
                        values.admin_fee = formatToFloat(values.admin_fee);

                        if (values.admin_fee == 0) {
                            values.admin_fee = 1000;
                        }
                    }

                    let submitData = {
                        user: current_staff,
                        url: current_booking?.edit_link !== null ? current_booking?.edit_link?.edit_url : current_booking.user.edit_url,
                        property_id: values.property_id,
                        unit_type: values.unit_type,
                        unit_id: values.unit_id,
                        parking_fee: values.carport ? values.parking_fee : null,
                        monthly_rental: formatToFloat(values.monthly_rental),
                        deposit_price: formatToFloat(values.deposit_price),
                        escalation: formatToFloat(values.escalation),
                        lease_term: values.lease_term,
                        deposit_type: values.deposit_type,
                        surance_amount: formatToFloat(values.surance_amount),
                        booking_fee_price: formatToFloat(values.booking_fee_price),
                        admin_fee: formatToFloat(values.admin_fee),
                        key_deposit: formatToFloat(values.key_deposit),
                        effluent: values.effluent ? values.effluent : default_values ? default_values.effluent : null,
                        refuse: values.refuse ? values.refuse : default_values ? default_values.refuse : null,
                        special_deduction: formatToFloat(values.special_deduction),
                        lease_surance: values.lease_surance,
                        carport: values.carport,
                        invoice_monthly_hide: values.invoice_monthly_hide,
                        invoice_monthly_total: values.invoice_monthly_total,
                        monthly_rental_wording: values.monthly_rental_wording,
                        special_wording: values.special_wording,
                        lease_start: values.lease_start,
                        lease_end: values.lease_end,
                        occupation_date: values.occupation_date,
                        size_required: values.size_required,
                        paid: values.paid,
                        paid_at: values.paid_at,
                        deposit_paid: values.deposit_paid,
                        deposit_paid_at: values.deposit_paid_at,
                        partially_paid: values.partially_paid,
                        partially_paid_at: values.partially_paid_at,
                        signed_at: values.signed_at,
                        signed_lease_at: values.signed_lease_at,
                        signed_lease_joint_at: values.signed_lease_joint_at,
                        has_gas: values.has_gas,
                        type: type,
                        tag_id: tag_id,
                        property_override: override_property,
                        price_override: values.price_override,
                        lease_dates_override: values.lease_dates_override,
                        special_payment_date: values.special_payment_date,
                    };

                    await axios({
                        method: "put",
                        url: `${process.env.REACT_APP_API_URL}user`,
                        data: submitData,
                    })
                        .then(() => {
                            setIgnoreRefresh(true);
                            dispatch(getBooking(current_booking.id));
                            toast(<NotificationToast title="Success" message="Data has been updated sucessfully" />);
                        })
                        .catch((error) => {
                            toast(<NotificationToast title="Something went wrong" message="" type="error" />);
                        });

                    setSubmitting(false);
                }}
            >
                {({ values, touched, errors, handleSubmit, setFieldValue, setFieldTouched, isValid, isSubmitting, dirty }) => {
                    return (
                        <Form onSubmit={handleSubmit} className="rounded-3xl">
                            <div className="pb-6 grid gap-x-2 gap-y-8 grid-cols-2">
                                {(process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "QP") && (
                                    <>
                                        <QuickFormSelect
                                            label="Building"
                                            name="property_id"
                                            getUnits={getUnits}
                                            getUnitTypes={getUnitTypes}
                                            onChange={(e) => {
                                                const newPropertyId = e.target.value;
                                                setFieldValue("property_id", newPropertyId);
                                                setSelectedPropertyId(newPropertyId);
                                            }}
                                            onBlur={setFieldTouched}
                                            value={current_booking?.property_id}
                                            touched={touched?.property_id}
                                            error={errors?.property_id}
                                            options={properties}
                                        />
                                        {process.env.REACT_APP_COLOUR === "QL" ? (
                                            <QuickFormSelect
                                                label="Unit Type"
                                                name="unit_type"
                                                onChange={setFieldValue}
                                                onBlur={setFieldTouched}
                                                value={values.unit_type}
                                                touched={touched.unit_type}
                                                error={errors.unit_type}
                                                options={unitTypes.length > 0 ? unitTypes : "none"}
                                            />
                                        ) : (
                                            ""
                                        )}
                                        <QuickFormSelect
                                            label="Unit"
                                            name="unit_id"
                                            onChange={setFieldValue}
                                            onBlur={setFieldTouched}
                                            value={values.unit_id}
                                            touched={touched.unit_id}
                                            error={errors.unit_id}
                                            options={units.length > 0 ? units : "none"}
                                        />
                                        {/* {console.log(current_booking)} */}
                                        {/* <QuickFormSelect
                                        label="Bed"
                                        name="bed_id"
                                        onChange={setFieldValue}
                                        onBlur={setFieldTouched}
                                        value={values.bed_id}
                                        touched={touched.bed_id}
                                        error={errors.bed_id}
                                        options={beds.length > 0 ? beds : "none"}
                                    /> */}

                                        {process.env.REACT_APP_COLOUR === "QL" && !isTenantOnly ? (
                                            <>
                                                <Field
                                                    name="carport"
                                                    as={QuickFormText}
                                                    label="Parking / Carport"
                                                    tooltip="Enter the Carport / Garage Number here, otherwise leave blank if not applicable"
                                                    onChange={(e) => {
                                                        const newCarport = e.target.value;
                                                        setFieldValue("carport", newCarport);
                                                        setSelectedCarport(newCarport);
                                                    }}
                                                />
                                                {values.carport && (
                                                    <>
                                                        <Field name="parking_fee" as={QuickFormText} label="Parking Fee" prepend="R" currency="true" disabled />
                                                    </>
                                                )}
                                                <Field name="monthly_rental_wording" as={QuickFormText} label="Monthly Wording" />
                                                <Field name="special_wording" as={QuickFormText} label="Special Wording" />
                                                <div className="col-span-2">
                                                    <Field
                                                        name="monthly_rental"
                                                        as={QuickFormText}
                                                        label="Monthly Rental"
                                                        prepend="R"
                                                        currency="true"
                                                        disabled={!canEditApplicantDetails}
                                                        onBlur={(e) => {
                                                            setFieldTouched("monthly_rental", true);
                                                            const calculatedDeposit = calculateDeposit(values.deposit_type, e.target.value);
                                                            if (calculatedDeposit !== null) {
                                                                setFieldValue("deposit_price", calculatedDeposit.toFixed(2));
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex flex-col">
                                                    <label htmlFor="deposit_type" className="text-sm text-dark_background">
                                                        Deposit Type
                                                    </label>
                                                    <select
                                                        name="deposit_type"
                                                        value={values.deposit_type}
                                                        onChange={(e) => {
                                                            const value = parseInt(e.target.value);
                                                            setFieldValue("deposit_type", value);
                                                            const calculatedDeposit = calculateDeposit(value, values.monthly_rental);
                                                            if (calculatedDeposit !== null) {
                                                                setFieldValue("deposit_price", calculatedDeposit.toFixed(2));
                                                            }
                                                        }}
                                                        className="mt-1 block border font-light border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-primary sm:text-sm transition ease-linear duration-10 hover:border-primary"
                                                    >
                                                        {depositTypes.map((type) => (
                                                            <option key={type.value} value={type.value}>
                                                                {type.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <Field
                                                    name="deposit_price"
                                                    as={QuickFormText}
                                                    label="Deposit"
                                                    prepend="R"
                                                    currency="true"
                                                    disabled={values.deposit_type !== 4 ? true : !canEditApplicantDetails}
                                                />
                                            </>
                                        ) : (
                                            ""
                                        )}
                                    </>
                                )}

                                {process.env.REACT_APP_COLOUR === "UC" && (
                                    <div className="flex flex-col mt-1">
                                        {canEditApplicantDetails ? (
                                            <Field name="monthly_rental" as={QuickFormText} currency="true" hidden={false} label="Monthly Rental" />
                                        ) : (
                                            <>
                                                <Field name="monthly_rental" as={QuickFormText} currency="true" hidden={true} />
                                                <label className="text-sm text-dark_background">Monthly Rental</label>
                                                <div className="flex rounded-md mt-3">
                                                    <span className="inline-flex items-center px-1 rounded-l-md font-light text-sm">
                                                        R{" "}
                                                        {current_booking.rates[current_booking.rates.length - 1] && current_booking.rates[current_booking.rates.length - 1].monthly_rental
                                                            ? current_booking.rates[current_booking.rates.length - 1].monthly_rental
                                                            : ""}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {process.env.REACT_APP_COLOUR === "QP" && (
                                    <div className="flex flex-col mt-1">
                                        <Field name="monthly_rental" as={QuickFormText} prepend="R" currency="true" label="Monthly Rental" disabled={!canEditApplicantDetails} />
                                    </div>
                                )}

                                {process.env.REACT_APP_COLOUR === "QP" ? (
                                    <>
                                        <Field name="escalation" type="text" as={QuickFormText} label="Escalation" prepend="%" currency="true" />
                                        <div className="flex flex-col mt-1">
                                            <SettingsSelect
                                                label="Lease Term"
                                                name="lease_term"
                                                onChange={setFieldValue}
                                                onBlur={setFieldTouched}
                                                value={values.lease_term}
                                                touched={touched.lease_term}
                                                error={errors.lease_term}
                                                options={leaseTerms}
                                            />
                                        </div>
                                        <div className="flex flex-col mt-1">
                                            <SettingsSelect
                                                label="Deposit"
                                                name="deposit_type"
                                                onChange={setFieldValue}
                                                onBlur={setFieldTouched}
                                                value={values.deposit_type}
                                                touched={touched.deposit_type}
                                                error={errors.deposit_type}
                                                options={depositList}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    process.env.REACT_APP_COLOUR !== "QL" && (
                                        <>
                                            <Field
                                                name="deposit_price"
                                                as={QuickFormText}
                                                label="Deposit"
                                                prepend="R"
                                                currency="true"
                                                disabled={!canEditApplicantDetails}
                                            />
                                        </>
                                    )
                                )}

                                {process.env.REACT_APP_COLOUR === "UC" && (
                                    <Field
                                        name="booking_fee"
                                        as={QuickFormText}
                                        label="Booking Fee"
                                        prepend="R"
                                        currency="true"
                                        disabled={!canEditApplicantDetails}
                                    />
                                )}

                                {process.env.REACT_APP_COLOUR === "QL" && !isTenantOnly && <Field name="surance_amount" as={QuickFormText} label="Deposit Waiver Amount" prepend="R" currency="true" disabled={true} />}
                            </div>

                            {/* All rental/lease/deposit/payment fields - hidden for Tenant-Only (they only set building/unit type/unit) */}
                            {!isTenantOnly && (
                            <>
                            {canEditApplicantDetails && process.env.REACT_APP_COLOUR == "UC" && (
                                <div className="flex flex-col mt-1">
                                    <Field name="price_override" as={SettingsSwitch} label="Override Monthly Rental" />
                                </div>
                            )}

                            {process.env.REACT_APP_COLOUR === "UC" && (
                                <>
                                    {current_booking.student_type == 4 || current_booking.student_type == 5 || current_booking.student_type == 6 || current_booking.student_type == 1 ? (
                                        <>
                                            <CustomButton
                                                text="Make NSFAS Pricing"
                                                styling="w-full"
                                                icon={faFileInvoiceDollar}
                                                onClick={() => make_nsfas_pricing()}
                                                loading={nsfasButtonLoading}
                                                loadingtext={"Loading"}
                                            />
                                            <br />
                                        </>
                                    ) : (
                                        ""
                                    )}
                                </>
                            )}

                            {process.env.REACT_APP_COLOUR === "QL" ? (
                                <div className="space-y-4 grid-cols-12 mb-5">
                                    <Field name="lease_surance" as={SettingsSwitch} label="Deposit Waiver" description=" " />
                                    <Field name="has_gas" as={SettingsSwitch} label="Has Gas" description=" " />
                                    <Field name="no_admin_fee" as={SettingsSwitch} label="No Admin Fee" description=" " />
                                </div>
                            ) : (
                                ""
                            )}

                            <div className="pb-6 grid gap-x-2 gap-y-8 grid-cols-2">
                                {process.env.REACT_APP_COLOUR === "QL" && (
                                    <>
                                        <Field name="admin_fee" as={QuickFormText} label="Admin Fee" prepend="R" currency="true" disabled={values.no_admin_fee == 1} />
                                        <Field name="refuse" as={QuickFormText} label="Refuse" prepend="R" currency="true" />
                                        <Field name="effluent" as={QuickFormText} label="Effluent" prepend="R" currency="true" />
                                        <Field name="key_deposit" as={QuickFormText} label="Key Deposit" prepend="R" currency="true" />
                                        <Field name="special_deduction" as={QuickFormText} label="Deduction" prepend="R" currency="true" />
                                    </>
                                )}

                                {process.env.REACT_APP_COLOUR === "UC" || process.env.REACT_APP_COLOUR === "QL" ? (
                                    <>
                                        <Field name="lease_start" type="text" as={QuickFormDate} label="Lease Start Date" disabled={!canEditApplicantDetails} />
                                        <Field name="lease_end" type="text" as={QuickFormDate} label="Lease End Date" disabled={!canEditApplicantDetails} />
                                    </>
                                ) : (
                                    <>
                                        <Field name="occupation_date" type="text" as={QuickFormDate} label="Occupation Date" />
                                        <Field name="size_required" type="number" as={QuickFormText} label="Size Required (SQM)" />
                                    </>
                                )}
                            </div>

                            {(process.env.REACT_APP_COLOUR === "UC" || process.env.REACT_APP_COLOUR === "QL") && canEditApplicantDetails && (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <label className="block text-sm font-medium text-gray-700 mb-3">Calculate Lease End Date</label>
                                    <div className="flex items-center gap-4 mb-3">
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="radio"
                                                name="leaseTermCalculator"
                                                value="6"
                                                checked={leaseTermMonths === 6}
                                                onChange={() => setLeaseTermMonths(6)}
                                                className="mr-2 h-4 w-4 text-primary focus:ring-primary border-gray-300"
                                            />
                                            <span className="text-sm text-gray-700">6 Months</span>
                                        </label>
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="radio"
                                                name="leaseTermCalculator"
                                                value="12"
                                                checked={leaseTermMonths === 12}
                                                onChange={() => setLeaseTermMonths(12)}
                                                className="mr-2 h-4 w-4 text-primary focus:ring-primary border-gray-300"
                                            />
                                            <span className="text-sm text-gray-700">1 Year</span>
                                        </label>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (values.lease_start) {
                                                const startDate = moment(values.lease_start);
                                                const endDate = startDate.clone().add(leaseTermMonths, "months").subtract(1, "days");
                                                setFieldValue("lease_end", endDate.format("YYYY-MM-DD"));
                                            } else {
                                                toast(<NotificationToast title="Missing Start Date" message="Please select a Lease Start Date first." type="warning" />);
                                            }
                                        }}
                                        className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                                    >
                                        Calculate End Date
                                    </button>
                                </div>
                            )}

                            {canEditApplicantDetails && process.env.REACT_APP_COLOUR == "UC" && (
                                <div className="flex flex-col mb-4">
                                    <Field name="lease_dates_override" as={SettingsSwitch} label="Override Lease Dates" />
                                </div>
                            )}

                            {process.env.REACT_APP_COLOUR != "QP" ? (
                                <>
                                    <Field name="invoice_monthly_total" as={SettingsSwitch} label="Ignore monthly rental towards total" />
                                    <Field name="invoice_monthly_hide" as={SettingsSwitch} label="Hide Monthly Rental" />
                                </>
                            ) : (
                                ""
                            )}

                            {process.env.REACT_APP_COLOUR === "UC" && (
                                <>
                                    {current_booking?.payfast && current_booking?.payfast.length > 0 ? (
                                        <div class="border px-1 mt-2">
                                            <p className="font-bold italic underline">PayFast Last Payment</p>
                                            <p>
                                                <b>Status:</b> {current_booking?.payfast[current_booking?.payfast.length - 1].payment_status}
                                                <br />
                                                <b>Description:</b> {current_booking?.payfast[current_booking?.payfast.length - 1].item_description}
                                                <br />
                                                <b>Amount:</b> R {current_booking?.payfast[current_booking?.payfast.length - 1].amount_gross}
                                            </p>
                                        </div>
                                    ) : (
                                        ""
                                    )}
                                </>
                            )}

                            <div className="space-y-4 sm:grid sm:grid-cols-6 gap-2">
                                {process.env.REACT_APP_COLOUR === "UC" && (
                                    <>
                                        <Field name="paid" as={SettingsSwitch} label="Booking Fee Paid" description=" " />
                                        <Field name="paid_at" type="text" as={QuickFormDate} label="At" />
                                    </>
                                )}

                                <Field name="deposit_paid" as={SettingsSwitch} label="Deposit Paid" description=" " />
                                <Field name="deposit_paid_at" type="text" as={QuickFormDate} label="At" />

                                <Field name="partially_paid" as={SettingsSwitch} label="Partially Paid" description=" " />
                                <Field name="partially_paid_at" type="text" as={QuickFormDate} label="At" />

                                {process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "QP" || process.env.REACT_APP_COLOUR === "UC" ? (
                                    <>
                                        <Field name="signed_at" as={SettingsSwitch} label="Lease Signed" description=" " />
                                        <Field name="signed_lease_at" type="text" as={QuickFormDate} label="At" />
                                    </>
                                ) : (
                                    ""
                                )}

                            </div>

                            {process.env.REACT_APP_COLOUR === "UC" && current_booking.bed_id > 0 ? (
                                <div className="mt-5">
                                    <Field name="override_property_int" as={SettingsSwitch} label="Property Address Override" description=" " />

                                    {values.override_property_int === 1 ? (
                                        <QuickFormSelect label="Building Override Address" name="property_override" onChange={setFieldValue} value={values.property_override} options={properties} />
                                    ) : (
                                        ""
                                    )}
                                </div>
                            ) : (
                                ""
                            )}

                            {values.joint_application !== 0 && <Field name="signed_lease_joint_at" type="text" as={QuickFormDate} label="Joint Signed Lease At" />}

                            {current_staff.isSuper == 1 && (
                                <div className="mt-5">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Special Payment Date
                                    </label>
                                    <Field
                                        name="special_payment_date"
                                        type="number"
                                        min="1"
                                        max="31"
                                        placeholder="Day of month (1-31)"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Set the day of month (1-31) when this booking should be treated as month-end for collections. If set to 5, and WiFi cuts happen on 3rd, this booking gets cut on 8th instead. Leave empty for standard processing.
                                    </p>
                                </div>
                            )}
                            </>
                            )}
                            <br />
                            {!current_staff.building_manager ? (
                                <SubmitButton name="Save" disabled={!isValid || isSubmitting || !dirty} isSubmitting={isSubmitting} icon={faSave} text="Update" style={{ width: "100%" }} />
                            ) : (
                                ""
                            )}
                        </Form>
                    );
                }}
            </Formik>
            {/* Document generation / lease actions - hidden for Tenant-Only */}
            {!isTenantOnly && (
            <div className="border-t-2 border-white mt-2 py-2 w-auto">
                {/* Must check if property and all data is set */}
                {!current_staff.building_manager ? (
                    <>
                        {/* {process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "UC" && current_booking.bed_id > 0 ? */}
                        {!(process.env.REACT_APP_COLOUR === "UC" && current_booking.property_id === 40) && (
                            <CustomButton
                                text="Generate Invoice"
                                styling="w-full"
                                icon={faFileInvoiceDollar}
                                onClick={() => generate_document("invoice")}
                                loading={invoiceButtonLoading ? 1 : 0}
                                loadingtext={invoiceButtonLoadingText}
                            />
                        )}

                        {process.env.REACT_APP_COLOUR === "UC" && current_booking.property_id !== 40 && (
                            <CustomButton
                                text="Generate & Send Invoice"
                                styling="w-full mt-2"
                                icon={faFileInvoiceDollar}
                                onClick={() => generate_document("send_invoice")}
                                loading={invoiceButtonLoading ? 1 : 0}
                                loadingtext={invoiceButtonLoadingText}
                            />
                        )}

                        {/* : "" } */}
                        {/*  || (process.env.REACT_APP_COLOUR === "UC" && current_booking.bed_id > 0) */}
                        {process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "QP" || process.env.REACT_APP_COLOUR === "UC" ? (
                            <CustomButton
                                text="Generate Lease"
                                styling="w-full mt-2"
                                icon={faFileSignature}
                                onClick={() => {
                                    generate_document("lease");
                                }}
                                loading={leaseButtonLoading ? 1 : 0}
                                loadingtext={leaseButtonLoadingText}
                            />
                        ) : (
                            ""
                        )}

                        {process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "QP" || process.env.REACT_APP_COLOUR === "UC" ? (
                            <CustomButton
                                text="Generate & Send Lease"
                                styling="w-full mt-2"
                                icon={faFileSignature}
                                onClick={() => {
                                    generate_document("lease_send");
                                }}
                                loading={leaseButtonLoading ? 1 : 0}
                                loadingtext={leaseButtonLoadingText}
                            />
                        ) : (
                            ""
                        )}

                        {process.env.REACT_APP_COLOUR === "QP" ? (
                            <CustomButton
                                text="Generate Surety"
                                styling="w-full mt-2"
                                icon={faTreasureChest}
                                onClick={() => {
                                    generate_document("surety");
                                }}
                                loading={suretyButtonLoading ? 1 : 0}
                                loadingtext={suretyButtonLoadingText}
                            />
                        ) : (
                            ""
                        )}

                        {process.env.REACT_APP_COLOUR === "QL" || (process.env.REACT_APP_COLOUR === "UC" && current_booking.bed_id > 0) ? (
                            <>
                                <CustomButton
                                    text="Lease Addendum"
                                    styling="w-full mt-2"
                                    icon={faFileSignature}
                                    onClick={() =>
                                        dispatch(
                                            openModal({
                                                modal_type: "ModalChangeUnit",
                                                modal_props: {
                                                    setDoc,
                                                    db,
                                                    units,
                                                    unitTypes,
                                                    current_staff,
                                                    getUnits,
                                                    getUnitTypes,
                                                },
                                            }),
                                        )
                                    }
                                />

                                {current_staff.id === 1 ? (
                                    <>
                                        <CustomButton
                                            text="Re-Generate Addendum"
                                            styling="w-full mt-2"
                                            icon={faFileSignature}
                                            onClick={() => {
                                                re_generate_addendum(current_booking.id);
                                            }}
                                            loading={leaseButtonLoading ? 1 : 0}
                                            loadingtext={leaseButtonLoadingText}
                                        />
                                    </>
                                ) : (
                                    ""
                                )}
                            </>
                        ) : (
                            ""
                        )}
                    </>
                ) : (
                    ""
                )}

                {/* Old Leases Button */}
                <CustomButton
                    text="View Old Leases"
                    styling="w-full mt-2"
                    icon={faFileAlt}
                    onClick={() => {
                        setShowOldLeases(true);
                        // Set the old rates when opening the modal
                        if (current_booking?.rates) {
                            setOldRates(current_booking.rates);
                        }
                    }}
                />

                {current_booking.invoice_url && !current_staff.building_manager && !(process.env.REACT_APP_COLOUR === "UC" && current_booking.property_id === 40) && (
                    <CustomButton
                        text="Email Invoice and Lease"
                        styling="w-full mt-2"
                        icon={faSquareEnvelope}
                        onClick={() => email_documents()}
                        loading={emailButtonLoading ? 1 : 0}
                        loadingtext={emailButtonLoadingText}
                    />
                )}

                {process.env.REACT_APP_COLOUR === "UC" && current_booking.invoice_url && !current_staff.building_manager && current_booking.property_id !== 40 && (
                    <CustomButton
                        text="Send Invoice PDF"
                        styling="w-full mt-2"
                        icon={faSquareEnvelope}
                        onClick={() => send_invoice_pdf()}
                        loading={emailButtonLoading ? 1 : 0}
                        loadingtext={leaseSendButtonLoading}
                    />
                )}

                {current_booking.lease_url && !current_staff.building_manager && (
                    <CustomButton
                        text="Send Lease PDF"
                        styling="w-full mt-2"
                        icon={faSquareEnvelope}
                        onClick={() => send_lease_pdf()}
                        loading={emailButtonLoading ? 1 : 0}
                        loadingtext={leaseSendButtonLoading}
                    />
                )}
            </div>
            )}

            {/* Tenant-Only Info Banner - moved to bottom of Invoice and Lease tab */}
            {isTenantOnly && (
                <div className="mt-4">
                    <div className="bg-gray-100 border-l-4 border-gray-400 p-3 rounded">
                        <div className="flex items-start">
                            <p className="text-sm text-gray-700">
                                <strong>Tenant Chat:</strong> Lease-specific actions are only available for Lease Holders. You can update the building, unit type and unit above.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Old Leases Modal */}
            {showOldLeases && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowOldLeases(false)}></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                            &#8203;
                        </span>

                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                        <div className="flex justify-between items-center">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                Old Leases
                                            </h3>
                                            <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" onClick={() => setShowOldLeases(false)}>
                                                <span className="sr-only">Close</span>
                                                <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                                            </button>
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500">Note: Download does not save the Lease to Romi.</p>
                                        <div className="mt-4 overflow-y-auto max-h-96">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Lease Period
                                                        </th>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Monthly Rent
                                                        </th>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Status
                                                        </th>
                                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {oldRates && oldRates.length > 0 ? (
                                                        oldRates.map((rate, index) => (
                                                            <tr key={index}>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    {rate.lease_start && rate.lease_end
                                                                        ? `${new Date(rate.lease_start).toLocaleDateString()} - ${new Date(rate.lease_end).toLocaleDateString()}`
                                                                        : "N/A"}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">R{rate.monthly_rental?.toLocaleString() || "0.00"}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rate.lease_status || "N/A"}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                    <div className="flex justify-end space-x-2">
                                                                        <button
                                                                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                                                                            onClick={() => {
                                                                                generate_document("lease", rate.id);
                                                                                setShowOldLeases(false);
                                                                            }}
                                                                        >
                                                                            Generate
                                                                        </button>
                                                                        <button
                                                                            className={`text-indigo-600 hover:text-indigo-900 ${downloadingRateId === rate.id ? "opacity-50 cursor-not-allowed" : ""}`}
                                                                            disabled={downloadingRateId === rate.id}
                                                                            onClick={() => {
                                                                                if (downloadingRateId !== rate.id) {
                                                                                    download_lease(rate.id);
                                                                                }
                                                                            }}
                                                                        >
                                                                            {downloadingRateId === rate.id ? "Downloading..." : "Download"}
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                                                                No lease records found
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowOldLeases(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
